import LabelTableCell from 'Components/LabelTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import type { Catalog } from 'Services/ProductManagement/Features/Catalog/types'
import { useGetSalesChannelParameterGroupQuery } from 'Services/ProductManagement/Features/Parameter/parameterApi'
import type { Tariff } from 'Services/ProductManagement/Features/Tariff/types'
import { datetimeFormatter, getValueFromOptions, showValueOrHyphen } from 'Util/formatters'
import getTranslation from 'Util/getTranslation'
import {
  CircularProgress,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer
} from '@mui/material'
import { t } from 'ttag'

const StyledTableCell = styled(LabelTableCell)`
  width: 40%;
`

interface TariffCatalogDetailsProps {
  catalog: Catalog
  tariff: Tariff
  singleLoading: boolean
}

function TariffCatalogDetails({
  catalog,
  tariff,
  singleLoading
}: Readonly<TariffCatalogDetailsProps>) {
  const { getCodespaceNameByCode } = useAuthorityContext()

  const {
    data: salesChannelParameterGroup = null,
    isLoading: isSalesChannelParameterGroupLoading
  } = useGetSalesChannelParameterGroupQuery()

  const salesChannelOptions =
    salesChannelParameterGroup?.parameters.map(({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value ?? '',
      value: parameter_id
    })) ?? []

  return (
    <WithLoader loading={singleLoading}>
      <TableContainer component={Paper}>
        <Table sx={{ td: { border: 'none' } }} aria-label={t`Catalog details table`}>
          <TableBody>
            <StyledTableRow>
              <StyledTableCell>{t`Name`}</StyledTableCell>
              <TableCell>{catalog.name}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Catalog code`}</StyledTableCell>
              <TableCell>{catalog.catalog_code}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Description`}</StyledTableCell>
              <TableCell>{showValueOrHyphen(catalog.description)}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Parameter group`}</StyledTableCell>
              <TableCell>
                {isSalesChannelParameterGroupLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  getValueFromOptions(catalog.sales_channel, salesChannelOptions, '-')
                )}
              </TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Catalog owner`}</StyledTableCell>
              <TableCell>{getCodespaceNameByCode(catalog.catalog_owner)}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Products amount`}</StyledTableCell>
              <TableCell>{catalog.product_ids.length}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Tariff`}</StyledTableCell>
              <TableCell>{tariff.tariff_code}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Active from`}</StyledTableCell>
              <TableCell>{datetimeFormatter(tariff.active_period.valid_from)}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Active to`}</StyledTableCell>
              <TableCell>{datetimeFormatter(tariff.active_period.valid_until)}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Created`}</StyledTableCell>
              <TableCell>{datetimeFormatter(new Date(catalog.created_timestamp))}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Modified`}</StyledTableCell>
              <TableCell>{datetimeFormatter(new Date(catalog.modified_timestamp))}</TableCell>
            </StyledTableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </WithLoader>
  )
}

export default TariffCatalogDetails
